import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaTimes, FaSave, FaArrowRight, FaArrowLeft, FaGripVertical,
  FaSort, FaSortUp, FaSortDown
} from 'react-icons/fa';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '../../hooks/useToast';
import { logger } from '../../utils/logger';

/**
 * SortableItem - Componente per un campo sortable con drag & drop
 */
const SortableItem = ({ campo, index, isSelected, themeColor, onChangeTipoOrdinamento, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: campo.CAM_Id_Campi });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        p-2 mb-1.5 rounded border transition-all duration-200
        ${isDragging 
          ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/30 shadow-lg` 
          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500'
        }
      `}
    >
      <div className="flex items-center space-x-2">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0">
          <FaGripVertical className="text-gray-400 dark:text-gray-500 text-sm" />
        </div>
        
        {isSelected && (
          <span className={`
            flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center
            bg-${themeColor}-100 dark:bg-${themeColor}-900/30 
            text-${themeColor}-700 dark:text-${themeColor}-300 font-bold text-xs
          `}>
            {index + 1}
          </span>
        )}
        
        <div className="flex-grow min-w-0">
          <div className="text-sm text-gray-900 dark:text-gray-100">
            <span className="font-medium">{campo.CAM_Nome}</span>
            {campo.CAM_Nome === 'Id' && campo.CAM_Descrizione && campo.CAM_Descrizione !== 'Id' && (
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                ({campo.CAM_Descrizione})
              </span>
            )}
          </div>
        </div>

        {isSelected && onChangeTipoOrdinamento && (
          <>
            <select
              value={campo.CAM_TipoOrdinamento || ''}
              onChange={(e) => {
                e.stopPropagation();
                onChangeTipoOrdinamento(campo.CAM_Id_Campi, e.target.value);
              }}
              className={`
                px-2 py-1 text-sm rounded border transition-colors flex-shrink-0
                ${!campo.CAM_TipoOrdinamento 
                  ? 'border-red-400 bg-red-50 dark:bg-red-900/40 dark:border-red-500 text-gray-900 dark:text-red-100' 
                  : 'border-gray-400 dark:border-gray-400 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white'
                }
              `}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">Seleziona...</option>
              <option value="ASC">ASC ↑</option>
              <option value="DESC">DESC ↓</option>
            </select>
            {onRemove && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(campo);
                }}
                className="flex-shrink-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                title="Rimuovi dalla selezione"
              >
                ×
              </button>
            )}
          </>
        )}

        {!isSelected && (
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            Seq: {campo.CAM_Sequenza}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * OrdinamentoCampiModal - Gestisce l'ordinamento dei campi con drag & drop
 * 
 * Permette di:
 * - Selezionare campi non tecnici dalla lista disponibile
 * - Trascinarli nell'area "Selezionati per ordinamento"
 * - Riordinare i campi selezionati
 * - Assegnare automaticamente CAM_ProgressivoOrdinamento (1, 2, 3...)
 * - Scegliere il tipo di ordinamento (ASC o DESC)
 */
const OrdinamentoCampiModal = ({
  isOpen,
  onClose,
  campi = [],
  onSave,
  themeColor = 'emerald'
}) => {
  const { showSuccess, showError } = useToast();
  
  // Stato per i campi disponibili (non tecnici)
  const [campiDisponibili, setCampiDisponibili] = useState([]);
  // Stato per i campi selezionati per l'ordinamento
  const [campiSelezionati, setCampiSelezionati] = useState([]);
  
  // Flag per indicare se ci sono modifiche non salvate
  const [hasChanges, setHasChanges] = useState(false);
  
  // Drag & drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Inizializza i campi quando la modale si apre
  useEffect(() => {
    if (!isOpen) return;

    // MODIFICATO: Mostra TUTTI i campi (non solo quelli non tecnici)
    const tuttiCampi = [...campi];
    
    // Debug: controlla i primi campi per vedere se hanno descrizione
    if (tuttiCampi.length > 0) {
      logger.debug('[OrdinamentoCampiModal] Esempio primo campo:', {
        nome: tuttiCampi[0].CAM_Nome,
        descrizione: tuttiCampi[0].CAM_Descrizione,
        hasDescrizione: !!tuttiCampi[0].CAM_Descrizione,
        allKeys: Object.keys(tuttiCampi[0])
      });
    }

    // Separa i campi già configurati per l'ordinamento da quelli disponibili
    const conOrdinamento = tuttiCampi
      .filter(c => c.CAM_ProgressivoOrdinamento && c.CAM_ProgressivoOrdinamento > 0)
      .sort((a, b) => (a.CAM_ProgressivoOrdinamento || 0) - (b.CAM_ProgressivoOrdinamento || 0));

    const senzaOrdinamento = tuttiCampi
      .filter(c => !c.CAM_ProgressivoOrdinamento || c.CAM_ProgressivoOrdinamento === 0)
      .sort((a, b) => (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0));

    setCampiSelezionati(conOrdinamento);
    setCampiDisponibili(senzaOrdinamento);

    setHasChanges(false);

    logger.info('[OrdinamentoCampiModal] Inizializzazione:', {
      totali: tuttiCampi.length,
      conOrdinamento: conOrdinamento.length,
      senzaOrdinamento: senzaOrdinamento.length
    });
  }, [isOpen, campi]);

  // Gestione del drag & drop
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setHasChanges(true);

    // Trova quale campo è stato trascinato
    const activeInDisponibili = campiDisponibili.find(c => c.CAM_Id_Campi === active.id);
    const activeInSelezionati = campiSelezionati.find(c => c.CAM_Id_Campi === active.id);

    const overInDisponibili = campiDisponibili.find(c => c.CAM_Id_Campi === over.id);
    const overInSelezionati = campiSelezionati.find(c => c.CAM_Id_Campi === over.id);

    // Riordino all'interno della stessa lista
    if (activeInSelezionati && overInSelezionati) {
      setCampiSelezionati((items) => {
        const oldIndex = items.findIndex(c => c.CAM_Id_Campi === active.id);
        const newIndex = items.findIndex(c => c.CAM_Id_Campi === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    } else if (activeInDisponibili && overInDisponibili) {
      setCampiDisponibili((items) => {
        const oldIndex = items.findIndex(c => c.CAM_Id_Campi === active.id);
        const newIndex = items.findIndex(c => c.CAM_Id_Campi === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    // Spostamento tra liste gestito separatamente con i pulsanti
  }, [campiDisponibili, campiSelezionati]);

  // Sposta tutti i campi disponibili nei selezionati
  const handleSelectAll = useCallback(() => {
    setCampiSelezionati(prev => [...prev, ...campiDisponibili]);
    setCampiDisponibili([]);
    setHasChanges(true);
  }, [campiDisponibili]);

  // Rimuovi tutti i campi selezionati (riportali a disponibili)
  const handleDeselectAll = useCallback(() => {
    setCampiDisponibili(prev => [...prev, ...campiSelezionati].sort((a, b) => 
      (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0)
    ));
    setCampiSelezionati([]);
    setHasChanges(true);
  }, [campiSelezionati]);

  // Validazione prima del salvataggio
  const validateBeforeSave = useCallback(() => {
    // Verifica che ogni campo selezionato abbia un tipo di ordinamento
    const campiSenzaTipo = campiSelezionati.filter(c => !c.CAM_TipoOrdinamento);
    if (campiSenzaTipo.length > 0) {
      showError(`Seleziona il tipo di ordinamento per tutti i campi selezionati (${campiSenzaTipo.length} campi mancanti)`);
      return false;
    }

    return true;
  }, [campiSelezionati, showError]);

  // Salva la configurazione dell'ordinamento
  const handleSave = useCallback(() => {
    if (!validateBeforeSave()) return;

    // Prepara i campi selezionati con progressivo ordinamento
    const campiConOrdinamento = campiSelezionati.map((campo, index) => ({
      CAM_Id_Campi: campo.CAM_Id_Campi,
      CAM_ProgressivoOrdinamento: index + 1,
      CAM_TipoOrdinamento: campo.CAM_TipoOrdinamento // Usa il tipo ordinamento specifico del campo
    }));

    // Prepara i campi disponibili (reset ordinamento)
    const campiSenzaOrdinamento = campiDisponibili.map(campo => ({
      CAM_Id_Campi: campo.CAM_Id_Campi,
      CAM_ProgressivoOrdinamento: 0,
      CAM_TipoOrdinamento: '' // Stringa vuota invece di null
    }));

    // Combina tutti i campi da aggiornare
    const tuttiCampiDaAggiornare = [...campiConOrdinamento, ...campiSenzaOrdinamento];

    logger.info('[OrdinamentoCampiModal] Salvataggio:', {
      selezionati: campiConOrdinamento.length,
      disponibili: campiSenzaOrdinamento.length,
      campiConOrdinamento: campiConOrdinamento.map(c => ({
        id: c.CAM_Id_Campi,
        progressivo: c.CAM_ProgressivoOrdinamento,
        tipo: c.CAM_TipoOrdinamento
      })),
      campiSenzaOrdinamento: campiSenzaOrdinamento.map(c => ({
        id: c.CAM_Id_Campi,
        progressivo: c.CAM_ProgressivoOrdinamento,
        tipo: c.CAM_TipoOrdinamento
      }))
    });

    // Chiama la callback di salvataggio
    onSave(tuttiCampiDaAggiornare);
    
    if (campiSelezionati.length === 0) {
      showSuccess('Ordinamento rimosso: tutti i campi sono stati resettati');
    } else {
      showSuccess(`Ordinamento salvato: ${campiSelezionati.length} campi configurati`);
    }
    
    // Chiudi la modale
    onClose();
  }, [campiSelezionati, campiDisponibili, onSave, onClose, showSuccess, validateBeforeSave]);

  // Sposta un campo da disponibili a selezionati
  const handleMoveCampoToSelezionati = useCallback((campo) => {
    setCampiDisponibili(prev => prev.filter(c => c.CAM_Id_Campi !== campo.CAM_Id_Campi));
    setCampiSelezionati(prev => [...prev, campo]);
    setHasChanges(true);
  }, []);

  // Sposta un campo da selezionati a disponibili
  const handleMoveCampoToDisponibili = useCallback((campo) => {
    setCampiSelezionati(prev => prev.filter(c => c.CAM_Id_Campi !== campo.CAM_Id_Campi));
    setCampiDisponibili(prev => [...prev, campo].sort((a, b) => 
      (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0)
    ));
    setHasChanges(true);
  }, []);

  // Aggiorna il tipo di ordinamento per un campo specifico
  const handleChangeTipoOrdinamento = useCallback((campoId, nuovoTipo) => {
    setCampiSelezionati(prev => prev.map(campo => 
      campo.CAM_Id_Campi === campoId 
        ? { ...campo, CAM_TipoOrdinamento: nuovoTipo }
        : campo
    ));
    setHasChanges(true);
  }, []);

  // Componente per rendere un campo draggabile

  // Non renderizzare se la modale non è aperta
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl h-[85vh] flex flex-col">
        
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-800 dark:bg-gray-900 border-gray-700">
          <h2 className="text-xl font-bold text-white">
            Configurazione Ordinamento Campi
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-300 hover:text-white transition-colors"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        {/* Istruzioni */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Come funziona:</strong> Clicca sui campi nella lista "Disponibili" per aggiungerli alla lista "Selezionati per ordinamento". 
            I campi selezionati riceveranno automaticamente un progressivo (1, 2, 3...). 
            Riordina i campi trascinandoli nella posizione desiderata. 
            <strong className="ml-1">Per ogni campo, seleziona il tipo di ordinamento (ASC o DESC) dal menu a tendina.</strong>
          </p>
        </div>

        {/* Area Drag & Drop */}
        <div className="flex-grow overflow-hidden p-4">
          <div className="grid grid-cols-2 gap-4 h-full">
            
            {/* Lista Campi Disponibili */}
            <div className="flex flex-col h-full border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <div className="bg-gray-100 dark:bg-gray-700 p-3 border-b border-gray-300 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    Campi Disponibili ({campiDisponibili.length})
                  </h3>
                  <button
                    onClick={handleSelectAll}
                    disabled={campiDisponibili.length === 0}
                    className={`
                      px-3 py-1 text-xs rounded-md transition-colors
                      ${campiDisponibili.length === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
                      }
                    `}
                    title="Aggiungi tutti i campi all'ordinamento"
                  >
                    <FaArrowRight className="inline mr-1" />
                    Seleziona Tutti
                  </button>
                </div>
              </div>
              
              <div className="flex-grow overflow-y-auto p-2 bg-white dark:bg-gray-800">
                {campiDisponibili.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 py-8">
                    <FaSort className="mx-auto mb-2 text-3xl" />
                    <p className="text-sm">Nessun campo disponibile</p>
                    <p className="text-xs mt-1">Tutti i campi sono già selezionati</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5">
                    {campiDisponibili.map((campo, index) => (
                      <div
                        key={campo.CAM_Id_Campi}
                        onClick={() => handleMoveCampoToSelezionati(campo)}
                        className="p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-all"
                        title={campo.CAM_Descrizione || campo.CAM_Nome}
                      >
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          <span className="font-medium">{campo.CAM_Nome}</span>
                          {campo.CAM_Nome === 'Id' && campo.CAM_Descrizione && campo.CAM_Descrizione !== 'Id' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1 block">
                              ({campo.CAM_Descrizione})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Lista Campi Selezionati */}
            <div className="flex flex-col h-full border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <div className={`p-3 border-b bg-${themeColor}-100 dark:bg-gray-800 border-${themeColor}-300 dark:border-gray-700`}>
                <div className="flex justify-between items-center">
                  <h3 className={`font-semibold text-${themeColor}-800 dark:text-gray-100`}>
                    Selezionati per Ordinamento ({campiSelezionati.length})
                  </h3>
                  <button
                    onClick={handleDeselectAll}
                    disabled={campiSelezionati.length === 0}
                    className={`
                      px-3 py-1 text-xs rounded-md transition-colors
                      ${campiSelezionati.length === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                      }
                    `}
                    title="Rimuovi tutti i campi dall'ordinamento"
                  >
                    <FaArrowLeft className="inline mr-1" />
                    Rimuovi Tutti
                  </button>
                </div>
              </div>
              
              <div className={`flex-grow overflow-y-auto p-2 bg-white dark:bg-gray-800`}>
                {campiSelezionati.length === 0 ? (
                  <div className="text-center text-gray-400 dark:text-gray-500 py-8">
                    <FaArrowRight className="mx-auto mb-2 text-3xl" />
                    <p className="text-sm">Nessun campo selezionato</p>
                    <p className="text-xs mt-1">Clicca sui campi a sinistra per selezionarli</p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={campiSelezionati.map(c => c.CAM_Id_Campi)}
                      strategy={verticalListSortingStrategy}
                    >
                      {campiSelezionati.map((campo, index) => (
                        <SortableItem
                          key={campo.CAM_Id_Campi}
                          campo={campo}
                          index={index}
                          isSelected={true}
                          themeColor={themeColor}
                          onChangeTipoOrdinamento={handleChangeTipoOrdinamento}
                          onRemove={handleMoveCampoToDisponibili}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Footer con azioni */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {hasChanges && (
                <span className="text-yellow-600 dark:text-yellow-400">
                  ⚠️ Modifiche non salvate
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-5 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={campiSelezionati.some(c => !c.CAM_TipoOrdinamento)}
                className={`
                  px-5 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2
                  ${campiSelezionati.some(c => !c.CAM_TipoOrdinamento)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700 shadow-md`
                  }
                `}
                title={campiSelezionati.some(c => !c.CAM_TipoOrdinamento) ? 'Seleziona il tipo di ordinamento per tutti i campi selezionati' : campiSelezionati.length === 0 ? 'Salva e rimuovi ordinamento' : 'Salva la configurazione'}
              >
                <FaSave />
                <span>{campiSelezionati.length === 0 ? 'Rimuovi Ordinamento' : 'Salva Ordinamento'}</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrdinamentoCampiModal;
